import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { Brand, Category, Product } from "../../../types";
import { productService } from "../../../services/productService";
import { DeliveryArea } from "../../../services/deliveryAreaService";
import {
    answerDeskService,
    FaqEntry,
    FaqEntryInput,
    FaqImage,
    FaqTopic,
} from "../../../services/answerDeskService";
import { ImageUploadGrid } from "../../../components/media/ImageUploadGrid";
import { ALL_PLACEHOLDERS, apiErrorMessage, fillAnswer, needsProduct, TOPICS } from "../answerDeskUtils";
import { AnswerText } from "./AnswerCard";
import { AnswerImages } from "./AnswerImages";
import { ProductPicker } from "./ProductPicker";
import { loadProduct } from "../targetProducts";
import { hasIncompleteRule, newRule, Rule, rulesFromTargets, TargetsEditor, targetsFromRules } from "./TargetsEditor";

export interface EditorPrefill {
    question?: string;
    alt_phrasings?: string[];
    answer?: string;
    topic?: FaqTopic;
    is_general?: boolean;
    products?: Product[];
}

interface AnswerEditorDialogProps {
    open: boolean;
    /** Existing entry to edit; null for a new one. */
    entry: FaqEntry | null;
    prefill?: EditorPrefill;
    /** Products from the chat, used for the live preview. */
    previewProducts?: Product[];
    canPublish: boolean;
    deliveryAreas: DeliveryArea[];
    onClose: () => void;
    /** `entry` is null when it was deleted. */
    onSaved: (entry: FaqEntry | null, message: string) => void;
}

// Category and brand lists rarely change; load once per session.
let lookupCache: Promise<{ categories: Category[]; brands: Brand[] }> | null = null;
function loadLookups() {
    if (!lookupCache) {
        lookupCache = Promise.all([
            productService.getCategories({ limit: 1000, active_only: false }),
            productService.getBrands({ limit: 1000, active_only: false }),
        ])
            .then(([categories, brands]) => ({ categories, brands }))
            .catch((err) => {
                lookupCache = null;
                throw err;
            });
    }
    return lookupCache;
}

export const AnswerEditorDialog: React.FC<AnswerEditorDialogProps> = ({
    open,
    entry,
    prefill,
    previewProducts = [],
    canPublish,
    deliveryAreas,
    onClose,
    onSaved,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const answerRef = useRef<HTMLTextAreaElement | null>(null);
    const errorRef = useRef<HTMLDivElement | null>(null);

    const [question, setQuestion] = useState("");
    const [phrasings, setPhrasings] = useState<string[]>([]);
    const [topic, setTopic] = useState<FaqTopic>("OTHER");
    const [answer, setAnswer] = useState("");
    const [note, setNote] = useState("");
    const [isGeneral, setIsGeneral] = useState(false);
    const [isPinned, setIsPinned] = useState(false);
    const [rules, setRules] = useState<Rule[]>([]);
    const [images, setImages] = useState<FaqImage[]>([]);
    const [uploading, setUploading] = useState(false);
    const [previewProduct, setPreviewProduct] = useState<Product[]>([]);
    // Set once the user picks a preview product themselves, so the
    // automatic default below never overrides their choice.
    const [previewTouched, setPreviewTouched] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setSaving(false);
        if (entry) {
            setQuestion(entry.question);
            setPhrasings(entry.alt_phrasings);
            setTopic(entry.topic);
            setAnswer(entry.answer ?? "");
            setNote(entry.internal_note ?? "");
            setIsGeneral(entry.is_general);
            setIsPinned(entry.is_pinned);
            setRules(rulesFromTargets(entry.targets));
            setImages(entry.images ?? []);
        } else {
            setQuestion(prefill?.question ?? "");
            setPhrasings(prefill?.alt_phrasings ?? []);
            setTopic(prefill?.topic ?? (prefill?.products?.length ? "PRODUCT_INFO" : "OTHER"));
            setAnswer(prefill?.answer ?? "");
            setNote("");
            setIsGeneral(prefill?.is_general ?? false);
            setIsPinned(false);
            setRules(prefill?.products?.length ? [newRule("products", prefill.products)] : []);
            setImages([]);
        }
        setPreviewProduct(previewProducts.slice(0, 1));
        setPreviewTouched(false);
        loadLookups()
            .then(({ categories, brands }) => {
                setCategories(categories);
                setBrands(brands);
            })
            .catch(() => setError("Couldn't load categories and brands. You can still save, but the rules list may be empty."));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, entry]);

    // The Save/Publish buttons sit below a long form; bring a new error
    // into view instead of leaving it hidden at the top.
    useEffect(() => {
        if (error) errorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [error]);

    const insertPlaceholder = (key: string) => {
        const token = `{${key}}`;
        const el = answerRef.current;
        if (!el) {
            setAnswer((a) => a + token);
            return;
        }
        const start = el.selectionStart ?? answer.length;
        const end = el.selectionEnd ?? answer.length;
        const next = answer.slice(0, start) + token + answer.slice(end);
        setAnswer(next);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + token.length, start + token.length);
        });
    };

    const isPublished = entry?.status === "PUBLISHED";
    const isArchived = entry?.status === "ARCHIVED";
    const canDeleteOwn = !canPublish && !!entry && entry.is_mine && (entry.status === "REQUESTED" || entry.status === "DRAFT");

    const save = async (mode: "draft" | "publish" | "keep") => {
        setError(null);
        if (!question.trim()) {
            setError("Write the question first.");
            return;
        }
        if (hasIncompleteRule(rules) && !isGeneral) {
            setError("Finish or remove the empty “applies to” rule.");
            return;
        }
        const targets = isGeneral ? [] : targetsFromRules(rules);
        if (mode === "publish" || (mode === "keep" && isPublished)) {
            if (!answer.trim()) {
                setError("Write the answer before publishing.");
                return;
            }
            if (!isGeneral && targets.length === 0) {
                setError("Choose what this answer applies to, or mark it General.");
                return;
            }
        }

        const payload: FaqEntryInput = {
            question: question.trim(),
            alt_phrasings: phrasings,
            answer: answer.trim() ? answer : null,
            internal_note: note.trim() ? note : null,
            topic,
            is_general: isGeneral,
            targets,
            image_ids: images.map((img) => img.faq_image_id),
        };
        if (canPublish) {
            payload.is_pinned = isPinned;
            if (mode === "publish") payload.status = "PUBLISHED";
            else if (mode === "draft" && entry) payload.status = answer.trim() ? "DRAFT" : "REQUESTED";
        }

        setSaving(true);
        try {
            const saved = entry
                ? await answerDeskService.updateEntry(entry.faq_entry_id, payload)
                : await answerDeskService.createEntry(payload);
            const message =
                saved.status === "PUBLISHED"
                    ? mode === "publish" ? "Published — moderators can copy it now" : "Saved"
                    : saved.status === "REQUESTED"
                      ? canPublish ? "Saved as an open question" : "Sent to the review queue"
                      : canPublish ? "Saved as a draft" : "Sent for approval";
            onSaved(saved, message);
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't save. Check your connection and try again."));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!entry) return;
        const archivedAlready = entry.status === "ARCHIVED";
        const confirmText = canPublish
            ? archivedAlready
                ? "Delete this answer permanently?"
                : "Archive this answer? Moderators won't see it any more."
            : "Delete your question?";
        if (!window.confirm(confirmText)) return;
        setSaving(true);
        try {
            const res = await answerDeskService.deleteEntry(entry.faq_entry_id);
            onSaved(res.archived ? { ...entry, status: "ARCHIVED" } : null, res.archived ? "Archived" : "Deleted");
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't remove it. Try again."));
            setSaving(false);
        }
    };

    // With no product from the chat, preview with the answer's own product
    // (its first "Products" rule) — the same product it fills from when
    // copied. Loaded live so the preview shows real price and stock.
    const ruleProductId = (() => {
        const productsRule = rules.find((r) => r.kind === "products");
        return productsRule && productsRule.kind === "products" ? productsRule.products[0]?.product_id : undefined;
    })();
    const wantsPreviewProduct = needsProduct(answer);
    useEffect(() => {
        if (!open || previewTouched || previewProduct.length > 0 || !wantsPreviewProduct || !ruleProductId) return;
        let cancelled = false;
        loadProduct(ruleProductId).then((p) => {
            if (!cancelled && p) setPreviewProduct((current) => (current.length > 0 ? current : [p]));
        });
        return () => {
            cancelled = true;
        };
    }, [open, previewTouched, previewProduct.length, wantsPreviewProduct, ruleProductId]);

    const previewSegments = fillAnswer(answer, previewProduct[0] ?? null, deliveryAreas);
    const title = entry
        ? entry.status === "REQUESTED" && canPublish
            ? "Answer a question"
            : entry.status === "PUBLISHED"
              ? "Edit answer"
              : "Edit suggestion"
        : canPublish
          ? "New answer"
          : "Suggest an answer";
    const asker =
        entry && entry.status !== "PUBLISHED" && entry.created_by_email && !entry.is_mine
            ? `From ${entry.created_by_email}`
            : null;

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 2, pr: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                    {title}
                    {asker && (
                        <Typography variant="body2" color="text.secondary">
                            {asker}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} disabled={saving} aria-label="Close">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    <Stack spacing={2.25} sx={{ flex: "999 1 520px", minWidth: 0, p: { xs: 2, sm: 3 } }}>
                        {error && (
                            <Alert ref={errorRef} severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}
                        {entry?.flag_note && (
                            <Alert severity="warning">
                                <strong>Flagged{entry.flagged_by_email ? ` by ${entry.flagged_by_email}` : ""}:</strong> {entry.flag_note}
                            </Alert>
                        )}

                        <TextField
                            label="Question"
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            inputProps={{ maxLength: 300 }}
                            required
                            autoFocus={!entry}
                        />

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[] as string[]}
                                value={phrasings}
                                onChange={(_, v) => setPhrasings((v as string[]).map((s) => s.trim()).filter(Boolean))}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return <Chip key={key} size="small" label={option} {...tagProps} />;
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Other ways customers ask"
                                        placeholder={phrasings.length ? "" : "e.g. dam koto, দাম কত — press Enter after each"}
                                        helperText="Helps search find this answer"
                                    />
                                )}
                                sx={{ flex: 1, minWidth: 0 }}
                            />
                            <TextField
                                select
                                label="Topic"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value as FaqTopic)}
                                sx={{ minWidth: { sm: 200 } }}
                            >
                                {TOPICS.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Stack>

                        <Box>
                            <TextField
                                label="Answer (what the moderator copies)"
                                value={answer}
                                onChange={(e) => setAnswer(e.target.value)}
                                inputRef={answerRef}
                                multiline
                                minRows={4}
                                fullWidth
                                inputProps={{ maxLength: 5000 }}
                                helperText={canPublish ? undefined : "Leave empty if you only want to ask the question"}
                            />
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Insert live value:
                                </Typography>
                                {ALL_PLACEHOLDERS.map((p) => (
                                    <Chip
                                        key={p.key}
                                        label={p.label}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => insertPlaceholder(p.key)}
                                    />
                                ))}
                            </Stack>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Images <Typography component="span" variant="caption" color="text.secondary">(optional — size charts, how-to photos, labels)</Typography>
                            </Typography>
                            <ImageUploadGrid<FaqImage>
                                items={images}
                                onChange={setImages}
                                upload={(file) => answerDeskService.uploadImage(file)}
                                getId={(img) => img.faq_image_id}
                                getThumbnail={(img) => img.thumbnail_url}
                                getName={(img) => img.original_filename}
                                disabled={saving}
                                onBusyChange={setUploading}
                                describeError={(err) => apiErrorMessage(err, "Upload failed")}
                            />
                        </Box>

                        <TextField
                            label="Note for moderators (never copied to the customer)"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            multiline
                            minRows={1}
                            inputProps={{ maxLength: 2000 }}
                            sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#FFF9F0" } }}
                        />

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, bgcolor: "grey.50" }}>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ flex: "1 1 auto" }}>
                                    Applies to
                                </Typography>
                                <FormControlLabel
                                    control={<Switch checked={isGeneral} onChange={(e) => setIsGeneral(e.target.checked)} />}
                                    label={<Typography variant="body2">General — show in every chat</Typography>}
                                />
                                {canPublish && (
                                    <FormControlLabel
                                        control={<Switch checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />}
                                        label={<Typography variant="body2">Pin to top</Typography>}
                                    />
                                )}
                            </Stack>
                            {isGeneral ? (
                                <Typography variant="body2" color="text.secondary">
                                    Shows for every chat and in search. Turn this off to tie it to products, a category or a brand.
                                </Typography>
                            ) : (
                                <>
                                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1.25 }}>
                                        Any matching rule shows this answer. Brand + Category means both must match.
                                    </Typography>
                                    <TargetsEditor rules={rules} onChange={setRules} categories={categories} brands={brands} />
                                </>
                            )}
                        </Paper>
                    </Stack>

                    <Box
                        component="aside"
                        aria-label="Preview"
                        sx={{
                            flex: "1 1 320px",
                            minWidth: 0,
                            bgcolor: "grey.50",
                            borderLeft: { md: 1 },
                            borderTop: { xs: 1, md: 0 },
                            borderColor: "divider",
                            p: { xs: 2, sm: 3 },
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.5,
                        }}
                    >
                        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            Preview — what moderators copy
                        </Typography>
                        {needsProduct(answer) && (
                            <Box>
                                <ProductPicker
                                    size="small"
                                    label="Preview with product"
                                    value={previewProduct}
                                    onChange={(p) => {
                                        setPreviewTouched(true);
                                        setPreviewProduct(p.slice(-1));
                                    }}
                                    max={1}
                                />
                                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5 }}>
                                    Only for this preview — not saved. To tie the answer to a product, use “Applies to”.
                                </Typography>
                            </Box>
                        )}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                            {answer.trim() ? (
                                <AnswerText segments={previewSegments} dense />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    The answer will appear here.
                                </Typography>
                            )}
                            {images.length > 0 && (
                                <Box sx={{ mt: 1.5 }}>
                                    <AnswerImages images={images} question={question || "answer"} size={64} />
                                </Box>
                            )}
                        </Paper>
                        <Typography variant="caption" color="text.secondary">
                            Highlighted parts are filled live when a moderator copies: from the product in the chat, or from the product this answer is for. Price and stock are never out of date.
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1, flexWrap: "wrap" }}>
                {((canPublish && entry) || canDeleteOwn) && (
                    <Button color="error" onClick={remove} disabled={saving} sx={{ mr: "auto" }}>
                        {canPublish ? (isArchived ? "Delete permanently" : "Archive") : "Delete"}
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving} color="inherit">
                    Cancel
                </Button>
                {canPublish ? (
                    <>
                        {!isPublished && (
                            <Button variant="outlined" onClick={() => save("draft")} disabled={saving || uploading}>
                                Save draft
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={() => save(isPublished ? "keep" : "publish")}
                            disabled={saving || uploading}
                        >
                            {isPublished ? "Save changes" : "Publish"}
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" disableElevation onClick={() => save("keep")} disabled={saving || uploading}>
                        {answer.trim() ? "Send for approval" : "Send question"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
